import { Minus, Plus } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { cn } from '~/lib/utils';

interface NumberInputButtonProps {
  type: 'increment' | 'decrement';
  onClick: () => void;
  disabled?: boolean;
  className?: string;
}

export default function NumberInputButton({
  type,
  onClick,
  disabled = false,
  className,
}: NumberInputButtonProps) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  const baseButtonClassName = cn(
    'flex h-9 w-9 items-center justify-center rounded-md',
    'text-text-dark bg-bg-quaternary',
    'hover:not-disabled:bg-bg-tertiary hover:not-disabled:text-text-primary',
    'transition-colors',
    disabled && 'opacity-50 cursor-not-allowed pointer-events-none',
    className,
  );

  const handleMouseDown = () => {
    if (disabled) return;

    onClick();

    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        onClick();
      }, 50);
    }, 300);
  };

  const handleMouseUp = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const handleMouseLeave = () => {
    handleMouseUp();
  };

  const isIncrement = type === 'increment';

  return (
    <button
      type="button"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      className={baseButtonClassName}
      disabled={disabled}
      tabIndex={-1}
    >
      {isIncrement && <Plus className="h-4 w-4" />}
      {!isIncrement && <Minus className="h-4 w-4" />}
    </button>
  );
}
